import { useCallback, useMemo } from 'react';
import { Invoice, InvoicePayment, TerminalMode, TransactionType } from '@services';
import { InvoiceStatus } from '@models';
import { useCardPayment, useDemoGuard } from '@hooks';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { FormInstance } from 'antd/lib';

interface UsePaymentHandlersProps {
  invoice: Invoice | undefined;
  paymentForm: FormInstance<any>;
  invoiceTotal: number;
  paymentTotal: number;
  dueTotal: number;
  setModalType: (type: 'payment' | 'refund' | null) => void;
  onPaymentSuccess?: () => void;
}

export const usePaymentHandlers = ({
  invoice,
  paymentForm,
  invoiceTotal,
  paymentTotal,
  dueTotal,
  setModalType,
  onPaymentSuccess,
}: UsePaymentHandlersProps) => {
  const { invoiceStore, sessionStore } = useStore();
  const {
    processCardPayment,
    setCashPaymentLoading,
    resetPaymentState,
  } = useCardPayment();

  const { guard } = useDemoGuard();

  const currentUser = useMemo(() => {
    const user = sessionStore.currentLogin?.user;
    if (user) {
      return `${user.name || ''} ${user.surname || ''}`.trim();
    }
    return null;
  }, [sessionStore]);

  const handlePaymentCancel = useCallback(() => {
    setModalType(null);
    paymentForm.resetFields();
    resetPaymentState();
  }, [paymentForm, resetPaymentState, setModalType]);

  const handlePartialPaymentInput = useCallback((value: number) => {
    const roundedValue = Utils.roundToTwoDecimals(value);
    const maxAllowed = Utils.roundToTwoDecimals(invoiceTotal - paymentTotal);
    const validatedPaymentAmount = roundedValue > maxAllowed ? maxAllowed : roundedValue;

    paymentForm.setFieldsValue({ amount: Utils.roundToTwoDecimals(validatedPaymentAmount) });
  }, [invoiceTotal, paymentTotal, paymentForm]);

  const handlePaymentCreate = useCallback(async (values: InvoicePayment) => {
    if (!invoice) return;

    setCashPaymentLoading(true);
    const roundedAmount = Utils.roundToTwoDecimals(values.amount);

    await invoiceStore
      .createPayment({
        ...values,
        note: values.note || '',
        amount: roundedAmount,
        invoiceId: invoice.id,
        transactionType: values.transactionType || TransactionType.Payment,
        createdBy: currentUser || '',
      })
      .then(async (result) => {
        setModalType(null);
        paymentForm.resetFields();

        if (!result) return;

        const roundedResultAmount = Utils.roundToTwoDecimals(result.amount);
        const remainingAmount = Utils.roundToTwoDecimals(dueTotal - roundedResultAmount);
        const isInvoicePaid = remainingAmount <= 0;

        if (isInvoicePaid && invoice.status !== InvoiceStatus.Paid) {
          await invoiceStore.update({ status: InvoiceStatus.Paid, id: invoice.id });
        }

        onPaymentSuccess?.();
        setCashPaymentLoading(false);
      });
  }, [invoice, invoiceStore, paymentForm, dueTotal, paymentTotal, setCashPaymentLoading, setModalType, onPaymentSuccess, currentUser]);

  const handlePosPayment = useMemo(() => guard(async (values: InvoicePayment) => {
    if (!invoice) return;

    await processCardPayment(
      invoice.id,
      values.amount,
      TerminalMode.Sale,
      async (paymentData: InvoicePayment) => {
        paymentData.transactionType = TransactionType.Payment;
        await handlePaymentCreate(paymentData);
      }
    );
  }), [invoice, processCardPayment, handlePaymentCreate, guard]);

  const handleCashRefundCreate = useCallback(async (values: InvoicePayment) => {
    if (!invoice) return;

    setCashPaymentLoading(true);
    const roundedAmount = Utils.roundToTwoDecimals(values.amount);

    await invoiceStore
      .createPayment({
        ...values,
        note: values.note || '',
        amount: roundedAmount,
        invoiceId: invoice.id,
        transactionType: TransactionType.RefundPaid,
        createdBy: currentUser || '',
      })
      .then(async (result) => {
        setModalType(null);
        paymentForm.resetFields();

        if (!result) return;
        setCashPaymentLoading(false);
      });

    setCashPaymentLoading(false);
  }, [invoice, invoiceStore, paymentForm, paymentTotal, dueTotal, setCashPaymentLoading, setModalType, currentUser]);

  const handlePosRefund = useMemo(() => guard(async (values: InvoicePayment) => {
    if (!invoice) return;

    await processCardPayment(
      invoice.id,
      values.amount,
      TerminalMode.Refund,
      async (paymentData: InvoicePayment) => {
        paymentData.transactionType = TransactionType.RefundPaid;
        paymentData.method = 'CardRefund';
        await handlePaymentCreate(paymentData);
      }
    );
  }), [invoice, processCardPayment, handlePaymentCreate, guard]);

  return {
    handlePaymentCancel,
    handlePartialPaymentInput,
    handlePaymentCreate,
    handlePosPayment,
    handleCashRefundCreate,
    handlePosRefund,
  };
};